import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { AircraftType } from '../../domain/models';
import type { HubAirportView } from '../../domain/infrastructure/types';
import type {
  AirportView,
  CompetitorActionView,
  FleetAircraftView,
  FleetAcquisitionView,
  FlightStatusSnapshot,
  FuelPriceState,
  LedgerSnapshot,
  LegView,
  MacroEvent,
  MaintenanceEvent,
  OriginDemandAnswer,
  RouteAnalyticsView,
  RouteSeatShareView,
  SaveGameState,
  SimClock,
  SlotAuctionAirportView,
  UICommand,
  WorkerEvent,
} from '../../domain/models/contracts';
import type { RouteMarketPreview } from '../../domain/routing/routePlannerEngine';
import type { MarketReachView, MarketSurveyRow } from '../../domain/demand/marketViews';
import { parseSaveGame } from '../../domain/save/saveGame';
import { downloadSave, readSlot, writeSlot } from '../persistence/saveStore';
import type { SaveSlot } from '../persistence/saveStore';

/* ------------------------------------------------------------------ */
/* State shape                                                         */
/* ------------------------------------------------------------------ */
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface RouteAlert {
  id: string;
  severity: AlertSeverity;
  legId: string | null;
  title: string;
  detail: string;
  /** Completed-day count at which the alert was raised. */
  day: number;
}

export interface SimulationUIState {
  /** World data has arrived; the base picker can be shown. */
  worldLoaded: boolean;
  /** A company is running (a new game, or a loaded save). The dashboard shows only when true. */
  ready: boolean;
  running: boolean;
  speedMultiplier: number;
  /** Absolute simulation hour (hours since SIM_EPOCH). */
  hour: number;
  completedDays: number;
  /** `performance.now()` at which the latest TICK_HOURLY arrived; used for smooth map interpolation. */
  tickReceivedAtMs: number;
  hqAirportId: string;
  /** Simulation clock with minute resolution, derived from the hourly tick. */
  clock: SimClock;
  airports: AirportView[];
  aircraftTypes: AircraftType[];
  legs: LegView[];
  maintenance: MaintenanceEvent[];
  fleet: FleetAircraftView[];
  /** Secondary listings, factory orders and leases from the fleet acquisition engine. */
  acquisitions: FleetAcquisitionView;
  /** Hub cards (tier, progress, priced projects, MRO income) for the Hubs & Infrastructure screen. */
  hubs: HubAirportView[];
  activeFlights: FlightStatusSnapshot[];
  analytics: RouteAnalyticsView[];
  ledger: LedgerSnapshot | null;
  /** Macro events in force (announced, active or resolving) as of the latest daily close. */
  macroEvents: MacroEvent[];
  fuel: FuelPriceState | null;
  competitorShares: RouteSeatShareView[];
  /** Latest AI carrier moves, newest first. */
  competitorActions: CompetitorActionView[];
  /** Slot auction results from the latest competitor cycle, one entry per contested airport. */
  slotAuctions: readonly SlotAuctionAirportView[];
  /** Sim day the slotAuctions were cleared on; 0 before the first cycle. */
  slotAuctionDay: number;
  /** Latest answer to requestRoutePreview. The panel ignores it unless the pair matches its selection. */
  routePreview: RouteMarketPreview | null;
  /** Latest answers to requestMarketReach and requestMarketSurvey. */
  marketReach: MarketReachView | null;
  marketSurvey: MarketSurveyRow[];
  /** Latest answer to requestOriginDemand. The demand inspector ignores it unless the origin matches. */
  originDemand: OriginDemandAnswer | null;
  /** Completed day of the most recent autosave written to this browser, or null before the first one. */
  lastAutosaveDay: number | null;
  /** One line about the last save, load or export; null when there is nothing to say. */
  saveNotice: string | null;
  alerts: RouteAlert[];
  lastError: string | null;
}

export interface SimulationControls {
  start: () => void;
  pause: () => void;
  setSpeed: (speedMultiplier: number) => void;
  /** Sends any command to the simulation worker (schedule grid, cabin editor, fare controls). */
  dispatch: (command: UICommand) => void;
  /** Asks the worker for a market preview of an unflown pair; the answer lands in state.routePreview. */
  requestRoutePreview: (originId: string, destinationId: string) => void;
  requestMarketReach: (airportId: string) => void;
  requestMarketSurvey: (originId: string | null) => void;
  /** Asks for one origin's demand to every airport; the answer lands in state.originDemand. */
  requestOriginDemand: (originId: string) => void;
  dismissError: () => void;
  /** Saves the current game to this browser (IndexedDB, `manual` slot). */
  saveGame: () => void;
  /** Loads the `manual` slot from this browser. */
  loadGame: () => void;
  /** Downloads the current game as a .json file. */
  exportSave: () => void;
  /** Loads a game from a .json file the player picked. Reports a notice when the file is not a save. */
  importSave: (file: Blob) => void;
  /** Starts a fresh company at the chosen base. */
  newGame: (hqAirportId: string) => void;
  /** Returns to the base picker. The clock pauses; the current game is discarded once a new base is chosen. */
  resetToBasePicker: () => void;
}

const MAX_ALERTS = 40;
const LOW_LOAD_FACTOR_PCT = 45;
const CAPACITY_CONSTRAINED_LOAD_FACTOR_PCT = 97;
export const DEFAULT_SPEED_MULTIPLIER = 1;
/**
 * Minimum wall-clock gap between autosaves. At 1x a day takes 24 s, so every
 * day is saved; at 24x the gap keeps a snapshot from being written every 40 ms.
 */
const AUTOSAVE_MIN_INTERVAL_MS = 10_000;

const EMPTY_ACQUISITIONS: FleetAcquisitionView = {
  market: { annualInterestRatePct: 0, demandIndex: 1 },
  catalogueMonth: 0,
  listings: [],
  factoryOrders: [],
  leases: [],
};

const initialState: SimulationUIState = {
  worldLoaded: false,
  ready: false,
  running: false,
  speedMultiplier: DEFAULT_SPEED_MULTIPLIER,
  hour: 0,
  completedDays: 0,
  tickReceivedAtMs: 0,
  hqAirportId: '',
  clock: { dayIndex: 0, minuteOfDay: 0 },
  airports: [],
  aircraftTypes: [],
  legs: [],
  maintenance: [],
  fleet: [],
  acquisitions: EMPTY_ACQUISITIONS,
  hubs: [],
  activeFlights: [],
  analytics: [],
  ledger: null,
  macroEvents: [],
  fuel: null,
  competitorShares: [],
  competitorActions: [],
  slotAuctions: [],
  slotAuctionDay: 0,
  routePreview: null,
  marketReach: null,
  marketSurvey: [],
  originDemand: null,
  lastAutosaveDay: null,
  saveNotice: null,
  alerts: [],
  lastError: null,
};

const MAX_COMPETITOR_ACTIONS = 40;

/* ------------------------------------------------------------------ */
/* Reducer                                                             */
/* ------------------------------------------------------------------ */
/** Where a save snapshot goes once the worker returns it. Answers arrive in request order, so a queue is enough. */
type ExportPurpose = 'autosave' | 'manual' | 'download';

type Action =
  | { type: 'EVENT'; event: WorkerEvent; receivedAtMs: number }
  | { type: 'WORKER_FAILED'; message: string }
  | { type: 'DISMISS_ERROR' }
  | { type: 'AUTOSAVED'; day: number }
  | { type: 'SAVE_NOTICE'; message: string | null }
  | { type: 'GAME_RESET' };

function deriveRouteAlerts(analytics: readonly RouteAnalyticsView[], day: number): RouteAlert[] {
  const alerts: RouteAlert[] = [];
  for (const a of analytics) {
    const route = `${a.origin}→${a.destination} (${a.aircraftReg})`;
    if (a.cancelledFlights > 0) {
      alerts.push({
        id: `${day}:${a.legId}:cancel`,
        severity: 'critical',
        legId: a.legId,
        title: `${route}: cancellations`,
        detail: `${a.cancelledFlights} of ${a.scheduledFlights} flights cancelled — aircraft in maintenance or out of position.`,
        day,
      });
    } else if (a.operatedFlights > 0 && a.loadFactorPct < LOW_LOAD_FACTOR_PCT) {
      alerts.push({
        id: `${day}:${a.legId}:lowlf`,
        severity: 'warning',
        legId: a.legId,
        title: `${route}: low load factor`,
        detail: `${a.loadFactorPct.toFixed(0)}% — consider lowering fares or cutting frequency.`,
        day,
      });
    } else if (a.operatedFlights > 0 && a.loadFactorPct >= CAPACITY_CONSTRAINED_LOAD_FACTOR_PCT) {
      alerts.push({
        id: `${day}:${a.legId}:full`,
        severity: 'info',
        legId: a.legId,
        title: `${route}: demand exceeds capacity`,
        detail: `${a.loadFactorPct.toFixed(0)}% load — raise fares or add capacity.`,
        day,
      });
    }
    if (a.operatedFlights > 0 && a.revenueUSD - a.operatingCostUSD < 0) {
      alerts.push({
        id: `${day}:${a.legId}:loss`,
        severity: 'warning',
        legId: a.legId,
        title: `${route}: loss-making`,
        detail: `Lost $${Math.round(a.operatingCostUSD - a.revenueUSD).toLocaleString('en-US')} on day ${day}.`,
        day,
      });
    }
  }
  return alerts;
}

function clockFromHour(hour: number): SimClock {
  return { dayIndex: Math.floor(hour / 24), minuteOfDay: (hour % 24) * 60 };
}

function applyEvent(state: SimulationUIState, event: WorkerEvent, receivedAtMs: number): SimulationUIState {
  switch (event.type) {
    case 'WORLD_LOADED':
      return { ...state, worldLoaded: true, airports: event.airports, aircraftTypes: event.aircraftTypes };
    case 'WORKER_READY': {
      // WORKER_READY also follows a load or a new game. Drop everything derived from the old world.
      const replaced = state.ready;
      return {
        ...state,
        ready: true,
        hour: event.hour,
        clock: clockFromHour(event.hour),
        completedDays: Math.floor(event.hour / 24),
        hqAirportId: event.hqAirportId,
        airports: event.airports,
        aircraftTypes: event.aircraftTypes,
        activeFlights: [],
        analytics: [],
        ledger: null,
        macroEvents: [],
        fuel: null,
        competitorShares: [],
        competitorActions: [],
        slotAuctions: [],
        slotAuctionDay: 0,
        routePreview: null,
        marketReach: null,
        marketSurvey: [],
        originDemand: null,
        alerts: [],
        saveNotice: replaced ? `World replaced; now at day ${Math.floor(event.hour / 24) + 1}` : state.saveNotice,
      };
    }
    case 'RUN_STATE':
      return { ...state, running: event.running, speedMultiplier: event.speedMultiplier };
    case 'NETWORK_STATE':
      return { ...state, hour: event.hour, clock: clockFromHour(event.hour), legs: event.legs, maintenance: event.maintenance, fleet: event.fleet, acquisitions: event.acquisitions, hubs: event.hubs, ledger: event.ledger };
    case 'TICK_HOURLY':
      return {
        ...state,
        hour: event.hour,
        clock: clockFromHour(event.hour),
        activeFlights: event.activeFlights,
        tickReceivedAtMs: receivedAtMs,
      };
    case 'TICK_DAILY': {
      const fresh = deriveRouteAlerts(event.analytics, event.day);
      return {
        ...state,
        completedDays: event.day,
        analytics: event.analytics,
        ledger: event.ledger,
        macroEvents: event.macroEvents,
        fuel: event.fuel,
        competitorShares: event.competitorShares,
        alerts: [...fresh, ...state.alerts].slice(0, MAX_ALERTS),
      };
    }
    case 'MACRO_EVENT_ANNOUNCED':
    case 'MACRO_EVENT_UPDATED':
      return {
        ...state,
        macroEvents: [...state.macroEvents.filter((known) => known.id !== event.event.id), event.event],
      };
    case 'FUEL_PRICE_CHANGED':
      return { ...state, fuel: event.fuel };
    case 'COMPETITOR_ACTION_LOGGED':
      return {
        ...state,
        competitorActions: [event.action, ...state.competitorActions].slice(0, MAX_COMPETITOR_ACTIONS),
      };
    case 'SLOT_AUCTIONS_CLEARED':
      return { ...state, slotAuctions: event.auctions, slotAuctionDay: event.day };
    case 'ROUTE_OPPORTUNITY_PREVIEW':
      return { ...state, routePreview: event.preview };
    case 'MARKET_REACH':
      return { ...state, marketReach: event.reach };
    case 'MARKET_SURVEY':
      return { ...state, marketSurvey: event.rows };
    case 'ORIGIN_DEMAND_ROWS':
      return { ...state, originDemand: event };
    case 'SAVE_GAME_EXPORT':
      // The snapshot is handled by the hook's export queue; the reducer only needs to acknowledge it.
      return state;
    case 'SIMULATION_ERROR':
      return {
        ...state,
        lastError: event.error,
        alerts: [
          {
            id: `err:${state.hour}:${event.error}`,
            severity: 'critical' as const,
            legId: null,
            title: 'Command rejected',
            detail: event.error,
            day: state.completedDays,
          },
          ...state.alerts,
        ].slice(0, MAX_ALERTS),
      };
    default: {
      const exhaustive: never = event;
      throw new Error(`Unhandled worker event ${JSON.stringify(exhaustive)}`);
    }
  }
}

function reducer(state: SimulationUIState, action: Action): SimulationUIState {
  switch (action.type) {
    case 'EVENT':
      return applyEvent(state, action.event, action.receivedAtMs);
    case 'WORKER_FAILED':
      return { ...state, running: false, lastError: `Simulation worker failed: ${action.message}` };
    case 'DISMISS_ERROR':
      return { ...state, lastError: null };
    case 'AUTOSAVED':
      return { ...state, lastAutosaveDay: action.day };
    case 'SAVE_NOTICE':
      return { ...state, saveNotice: action.message };
    case 'GAME_RESET':
      return { ...state, ready: false, running: false, legs: [], maintenance: [], fleet: [], activeFlights: [], analytics: [], ledger: null, alerts: [] };
    default: {
      const exhaustive: never = action;
      throw new Error(`Unhandled action ${JSON.stringify(exhaustive)}`);
    }
  }
}

function describeFailure(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */
export function useSimulationWorker(): { state: SimulationUIState; controls: SimulationControls } {
  const [state, dispatch] = useReducer(reducer, initialState);
  const workerRef = useRef<Worker | null>(null);
  const speedRef = useRef<number>(state.speedMultiplier);
  speedRef.current = state.speedMultiplier;
  const exportQueueRef = useRef<ExportPurpose[]>([]);
  const lastAutosaveAtRef = useRef<number>(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    const worker = new Worker(new URL('../../worker/sim.worker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;

    const notice = (message: string | null): void => dispatch({ type: 'SAVE_NOTICE', message });

    const storeExport = async (purpose: ExportPurpose, save: SaveGameState): Promise<void> => {
      const day = Math.floor(save.savedAtHour / 24) + 1;
      try {
        if (purpose === 'download') {
          downloadSave(save);
          notice(`Exported day ${day} as a .json file`);
          return;
        }
        const slot: SaveSlot = purpose === 'manual' ? 'manual' : 'autosave';
        await writeSlot(slot, save);
        if (purpose === 'manual') {
          notice(`Saved in this browser at day ${day}`);
        } else {
          dispatch({ type: 'AUTOSAVED', day });
        }
      } catch (error: unknown) {
        notice(`Could not write the save: ${describeFailure(error)}`);
      }
    };

    const onMessage = (message: MessageEvent<WorkerEvent>): void => {
      const event = message.data;
      if (event.type === 'TICK_DAILY') {
        // Autosave at most one request in flight, and no more often than the minimum interval.
        const now = performance.now();
        const queued = exportQueueRef.current.includes('autosave');
        if (!queued && now - lastAutosaveAtRef.current >= AUTOSAVE_MIN_INTERVAL_MS) {
          lastAutosaveAtRef.current = now;
          exportQueueRef.current.push('autosave');
          const request: UICommand = { type: 'SAVE_GAME_REQUEST' };
          worker.postMessage(request);
        }
      }
      if (event.type === 'SAVE_GAME_EXPORT') {
        const purpose: ExportPurpose = exportQueueRef.current.shift() ?? 'autosave';
        void storeExport(purpose, event.save);
      }
      dispatch({ type: 'EVENT', event, receivedAtMs: performance.now() });
    };
    const onError = (error: ErrorEvent): void => {
      dispatch({ type: 'WORKER_FAILED', message: error.message });
    };
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);

    return () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const send = useCallback((command: UICommand): void => {
    workerRef.current?.postMessage(command);
  }, []);

  const requestExport = useCallback(
    (purpose: ExportPurpose): void => {
      exportQueueRef.current.push(purpose);
      const request: UICommand = { type: 'SAVE_GAME_REQUEST' };
      send(request);
    },
    [send],
  );

  /** Validates a save before it reaches the worker, so a bad file never replaces the world. */
  const hydrate = useCallback(
    (raw: unknown): void => {
      const save = parseSaveGame(raw);
      const command: UICommand = { type: 'HYDRATE_GAME_STATE', save };
      send(command);
    },
    [send],
  );

  const reportFailure = useCallback((prefix: string, error: unknown): void => {
    dispatch({ type: 'SAVE_NOTICE', message: `${prefix}: ${describeFailure(error)}` });
  }, []);

  const controls: SimulationControls = {
    start: useCallback(() => send({ type: 'START_SIMULATION', speedMultiplier: speedRef.current }), [send]),
    pause: useCallback(() => send({ type: 'PAUSE_SIMULATION' }), [send]),
    setSpeed: useCallback(
      (speedMultiplier: number) => send({ type: 'SET_SPEED', speedMultiplier }),
      [send],
    ),
    dispatch: send,
    requestRoutePreview: useCallback(
      (originId: string, destinationId: string) => send({ type: 'PREVIEW_ROUTE_OPPORTUNITY', originId, destinationId }),
      [send],
    ),
    requestMarketReach: useCallback((airportId: string) => send({ type: 'REQUEST_MARKET_REACH', airportId }), [send]),
    requestMarketSurvey: useCallback((originId: string | null) => send({ type: 'REQUEST_MARKET_SURVEY', originId }), [send]),
    requestOriginDemand: useCallback(
      (originId: string) => send({ type: 'REQUEST_ORIGIN_DEMAND', originId }),
      [send],
    ),
    dismissError: useCallback(() => dispatch({ type: 'DISMISS_ERROR' }), []),
    saveGame: useCallback(() => requestExport('manual'), [requestExport]),
    exportSave: useCallback(() => requestExport('download'), [requestExport]),
    loadGame: useCallback(() => {
      void (async () => {
        try {
          const stored = await readSlot('manual');
          if (stored === null) {
            dispatch({ type: 'SAVE_NOTICE', message: 'No saved game in this browser yet' });
            return;
          }
          hydrate(stored.save);
        } catch (error: unknown) {
          reportFailure('Could not load the save', error);
        }
      })();
    }, [hydrate, reportFailure]),
    importSave: useCallback(
      (file: Blob) => {
        void (async () => {
          try {
            const raw: unknown = JSON.parse(await file.text());
            hydrate(raw);
          } catch (error: unknown) {
            reportFailure('That file is not a Flightline save', error);
          }
        })();
      },
      [hydrate, reportFailure],
    ),
    newGame: useCallback((hqAirportId: string) => send({ type: 'NEW_GAME', hqAirportId }), [send]),
    resetToBasePicker: useCallback(() => {
      send({ type: 'PAUSE_SIMULATION' });
      dispatch({ type: 'GAME_RESET' });
    }, [send]),
  };

  return { state, controls };
}
